import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { CARD_CHROME } from "../../primitives/card";

/**
 * Onboarding persona card, sharing the connectors-card anatomy: circular
 * icon puck, mono category eyebrow over a serif name, body copy at full
 * content width, and an optional chips row ("We watch ...") so options
 * stay comparable at a glance. Selection ripples back to the underlying
 * onboarding state so downstream steps can branch on the chosen persona.
 */
export interface PersonaCardProps {
  /** Mono category eyebrow rendered above the headline ("SLEEP", "GLP-1"). */
  eyebrow?: ReactNode;
  /** Optional glyph rendered inside the circular puck. */
  glyph?: ReactNode;
  headline: ReactNode;
  body?: ReactNode;
  /** Optional chips row; label defaults to "We watch". */
  chips?: string[];
  chipsLabel?: string;
  /** When true, the card renders the selected treatment. */
  selected?: boolean;
  /** Optional ambient tone hex - matched to the persona's emotional palette. */
  toneHex?: string;
  /** Heading level for the card title, so it follows whatever sits above it. */
  headingLevel?: 2 | 3 | 4;
  onClick?: () => void;
  className?: string;
}

export function PersonaCard({
  eyebrow,
  glyph,
  headline,
  body,
  chips,
  chipsLabel = "We watch",
  selected = false,
  toneHex,
  headingLevel = 3,
  onClick,
  className,
}: PersonaCardProps) {
  const tone = toneHex ?? "var(--color-teal-700)";
  const Heading = `h${headingLevel}` as const;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "group relative flex w-full flex-col items-start gap-2 text-left",
        "[transition:transform_160ms_var(--ease-out-expo),box-shadow_200ms_var(--ease-out-expo),border-color_200ms_var(--ease-out-expo)]",
        "hover:-translate-y-px active:scale-[0.98]",
        // Selected is a deliberate departure from the chrome; unselected
        // composes CARD_CHROME so surface-treatment changes flow through.
        selected
          ? "rounded-3xl border-2 border-teal-700 bg-teal-50/60 p-[15px] shadow-card-hover"
          : cn(CARD_CHROME, "p-4 hover:border-mist-dark hover:shadow-card-hover"),
        className,
      )}
    >
      <header className="flex w-full items-start gap-3">
        {glyph ? (
          <span
            aria-hidden="true"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white shadow-card"
            style={{
              color: tone,
              border: "1px solid color-mix(in srgb, currentColor 18%, transparent)",
            }}
          >
            {glyph}
          </span>
        ) : null}
        <span className="flex min-w-0 flex-col gap-0.5">
          {eyebrow ? (
            <span
              className="font-mono text-xs font-semibold uppercase tracking-[0.16em]"
              style={{ color: tone }}
            >
              {eyebrow}
            </span>
          ) : null}
          <Heading className="m-0 min-w-0 font-title text-balance text-[17px] font-medium leading-snug tracking-tight text-navy">
            {headline}
          </Heading>
        </span>
      </header>
      {body ? (
        <p className="m-0 max-w-[43ch] text-sm leading-relaxed text-gray-600">{body}</p>
      ) : null}
      {chips && chips.length > 0 ? (
        <span className="mt-auto flex w-full flex-col gap-1.5 pt-1">
          <span className="font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-gray-400">
            {chipsLabel}
          </span>
          <span className="flex flex-nowrap items-center gap-1.5">
            <span className="truncate rounded-full border border-mist-dark/40 bg-cream/50 px-2.5 py-0.5 text-xs leading-relaxed text-gray-700">
              {chips[0]}
            </span>
            {chips.length > 1 ? (
              <span className="shrink-0 whitespace-nowrap text-xs text-gray-400">
                + {chips.length - 1} more
              </span>
            ) : null}
          </span>
        </span>
      ) : null}
    </button>
  );
}
